import type {
  KnowledgeGeneratorCompetitorDraft,
  KnowledgeGeneratorLandmineDraft,
  KnowledgeGeneratorResearch,
  KnowledgeGeneratorSuggestion,
} from '../services/apiService';

export type SuggestionKind = 'objection' | 'websiteFaq' | 'aiGeneratedFaq';

/** Generated suggestions are additive. Manual phrases keep their order and
 * repeated requests cannot append duplicates or erase an admin's wording. */
export const mergeLandmineVariations = (manual: string[], generated: string[]): string[] => (
  [...new Set([...manual, ...generated].map((item) => item.trim()).filter(Boolean))]
);

export interface ReviewSuggestion extends KnowledgeGeneratorSuggestion {
  kind: SuggestionKind;
  voiceCaptured?: boolean;
  pending?: boolean;
  documentId?: string;
  starter?: boolean;
}

export interface CompanyProfileDraft {
  whatYouSell: string;
  targetAudience: string;
  howItWorks: string;
  certifications: string[];
  claimsReviewedAt: string | null;
  /** Who attested to this exact claim set, and which version they attested
   * to (see claimsReviewedVersion) — shown so the attestation names a real
   * person and a specific reviewed wording, not just a status flag. */
  claimsReviewedByEmail: string | null;
  claimsReviewedVersion: string | null;
  differentiators: string[];
  include: boolean;
}

export type ProfileFieldKey =
  | 'whatYouSell'
  | 'targetAudience'
  | 'differentiators'
  | 'howItWorks'
  | 'certifications';

export const buildCompanyProfileDraft = (
  research?: KnowledgeGeneratorResearch | null
): CompanyProfileDraft | null => {
  if (!research) return null;

  const whatYouSellParts: string[] = [];
  if (research.summary) whatYouSellParts.push(research.summary);
  if (research.products?.length) {
    whatYouSellParts.push(`Products/services: ${research.products.join(', ')}`);
  }

  const profile = {
    whatYouSell: whatYouSellParts.join(' ').trim(),
    targetAudience: (research.targetAudience || '').trim(),
    howItWorks: (research.howItWorks || '').trim(),
    certifications: (research.certifications || []).filter((item) => item?.trim()),
    claimsReviewedAt: research.claimsReviewedAt || null,
    claimsReviewedByEmail: research.claimsReviewedByEmail || null,
    claimsReviewedVersion: research.claimsReviewedVersion || null,
    differentiators: (research.keyFeatures || []).filter((item) => item?.trim()),
    include: true,
  };

  const hasContent =
    profile.whatYouSell ||
    profile.targetAudience ||
    profile.howItWorks ||
    profile.certifications.length > 0 ||
    profile.differentiators.length > 0;
  return hasContent ? profile : null;
};

export const toReviewList = (
  items: KnowledgeGeneratorSuggestion[] | undefined,
  kind: SuggestionKind
): ReviewSuggestion[] => (items || [])
  .filter((item) => !item.removed)
  .map((item) => ({ ...item, kind, voiceCaptured: item.source === 'voice' }));

export const markStarterSet = (items: ReviewSuggestion[]): ReviewSuggestion[] => {
  // The worker persists the first selection at generation time. Do not invent
  // confidence for legacy drafts and, crucially, do not overwrite a choice
  // returned by the server after a refresh or retry.
  return items.map((item) => ({ ...item, starter: item.confidence === 'high' && item.approved }));
};

/** User-authored/completed work stays immediately visible and ahead of the
 * generated starter suggestions. Unchecked drafts are still retained for
 * later review, but belong in a collapsed group below selected content. */
export const partitionReviewSuggestions = (
  items: ReviewSuggestion[]
): { visible: ReviewSuggestion[]; deferred: ReviewSuggestion[] } => {
  const userAuthored = (item: ReviewSuggestion) => (
    item.source === 'voice' || item.source === 'manual' || item.edited
  );
  const visible = items
    .filter((item) => item.approved || item.pending)
    .map((item, index) => ({ item, index }))
    .sort((left, right) => (
      Number(userAuthored(right.item)) - Number(userAuthored(left.item)) || left.index - right.index
    ))
    .map(({ item }) => item);
  const deferred = items.filter((item) => !item.approved && !item.pending);
  return { visible, deferred };
};

export const sectionOfKind = (kind: SuggestionKind): 'objections' | 'faqs' =>
  kind === 'objection' ? 'objections' : 'faqs';

export const reviewSignature = (
  objections: ReviewSuggestion[],
  faqs: ReviewSuggestion[],
  profile: CompanyProfileDraft | null,
  landmines: KnowledgeGeneratorLandmineDraft[] = [],
  competitors: KnowledgeGeneratorCompetitorDraft[] = []
): string =>
  JSON.stringify({
    suggestions: [...objections, ...faqs].map((item) => [
      item.kind,
      item.tempId,
      item.question,
      item.answer,
      item.category,
      item.approved,
      item.confidence,
    ]),
    landmines: landmines.map((item) => [
      item.tempId,
      item.trigger,
      item.approved,
      item.synced,
      item.canSay,
      item.cannotSay,
    ]),
    competitors: competitors.map((item) => [
      item.tempId,
      item.name,
      item.approved,
      item.synced,
      item.advantages,
      item.weaknesses,
    ]),
    profile: profile && [
      profile.whatYouSell,
      profile.targetAudience,
      profile.howItWorks,
      profile.certifications,
      profile.differentiators,
      profile.include,
    ],
  });

export const stripReviewFields = ({
  kind,
  voiceCaptured,
  pending,
  documentId,
  starter,
  ...suggestion
}: ReviewSuggestion): KnowledgeGeneratorSuggestion => suggestion;

export const isPublishableSuggestion = (item: ReviewSuggestion): boolean => !item.pending;

export const mergePolishedSuggestions = (
  current: ReviewSuggestion[],
  canonical: KnowledgeGeneratorSuggestion[] | undefined,
  kind: SuggestionKind
): ReviewSuggestion[] => {
  const byId = new Map((canonical || []).map((item) => [item.tempId, item]));
  return current.map((item) => {
    if (item.kind !== kind || item.edited || item.synced || item.pending) return item;
    const latest = byId.get(item.tempId);
    if (!latest) return item;
    return {
      ...item,
      ...latest,
      kind: item.kind,
      approved: item.approved,
      starter: item.starter,
      voiceCaptured: item.voiceCaptured,
      documentId: item.documentId,
    };
  });
};
